import { motion } from 'framer-motion'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FiActivity, FiCalendar, FiEdit2, FiMousePointer } from 'react-icons/fi'
import { IoMdStopwatch } from 'react-icons/io'
import { useParams } from 'react-router-dom'
import Badge from '../../../../../../components/ui/Badge'
import Card from '../../../../../../components/ui/Card'
import { useAppDispatch, useAppSelector } from '../../../../../../store/hooks'
import { selectMenuItemsAnalytics } from '../../../../../../store/selectors'
import { formatDate } from '../../../../../../utils/formatters'
import { MenuItem } from '../../../../../../utils/supabaseClient'

interface ItemStatisticsProps {
	cardVariants: {
		hidden: {
			opacity: number;
			y: number;
		};
		visible: {
			opacity: number;
			y: number;
		};
	},
	menuItem: MenuItem,
	analyticsAverages: {
		avgClicks: number;
		avgTimeSpent: number;
	} | {
		avgClicks: number;
		avgTimeSpent: string;
	},
	itemAnalytics: {
		id: string;
		year: number;
		month: number;
		click_count: number;
		time_spent: number;
	}[]
}

const ItemStatistics: React.FC<ItemStatisticsProps> = ({ cardVariants, menuItem, analyticsAverages, itemAnalytics }) => {
	const { t, i18n } = useTranslation()
	// const { branchId, itemId } = useParams<{ branchId: string; itemId: string }>();

	// const isLoadingAnalytics = useAppSelector(selectMenuItemLoadingAnalytics)
	// const analytics = useAppSelector(selectMenuItemsAnalytics);
	// const itemAnalytics = analytics[menuItem.id] ?? []
	// const dispatch = useAppDispatch()


	// useEffect(() => {
	// 	if (itemId && !isLoadingAnalytics && !itemAnalytics.length) {
	// 		dispatch(fetchMenuItemAnalytics(itemId))
	// 	}
	// }, [itemId, branchId]);

	return (
		<motion.div variants={cardVariants}>
			<Card className="p-6 border-none shadow-md">
				<div className="flex items-center gap-3 mb-6">
					<div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center">
						<FiActivity className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
					</div>
					<h3 className="text-lg font-semibold text-gray-900 dark:text-white">
						{t('menuItems.itemStatistics')}
					</h3>
				</div>

				<div className="space-y-4">
					<div className="flex items-center justify-between py-2">
						<div className="flex items-center gap-2">
							<FiCalendar className="w-4 h-4 text-gray-500" />
							<span className="text-sm text-gray-600 dark:text-gray-400">
								{t('menuItems.createdAt')}
							</span>
						</div>
						<span className="text-sm font-medium text-gray-900 dark:text-white">
							{formatDate(menuItem.created_at, i18n.language, {
								year: 'numeric',
								month: 'short',
								day: 'numeric'
							})}
						</span>
					</div>

					<div className="flex items-center justify-between py-2">
						<div className="flex items-center gap-2">
							<FiEdit2 className="w-4 h-4 text-gray-500" />
							<span className="text-sm text-gray-600 dark:text-gray-400">
								{t('menuItems.lastUpdated')}
							</span>
						</div>
						<span className="text-sm font-medium text-gray-900 dark:text-white">
							{formatDate(menuItem.updated_at || menuItem.created_at, i18n.language, {
								year: 'numeric',
								month: 'short',
								day: 'numeric'
							})}
						</span>
					</div>

					{menuItem.calories && (
						<div className="flex items-center justify-between py-2">
							<div className="flex items-center gap-2">
								<FiActivity className="w-4 h-4 text-gray-500" />
								<span className="text-sm text-gray-600 dark:text-gray-400">
									{t('menuItems.calories')}
								</span>
							</div>
							<span className="text-sm font-medium text-gray-900 dark:text-white">
								{menuItem.calories} {t('menuItems.cal')}
							</span>
						</div>
					)}

					{menuItem.spice_level && menuItem.spice_level !== 'none' && (
						<div className="flex items-center justify-between py-2">
							<div className="flex items-center gap-2">
								<span className="text-lg">🌶️</span>
								<span className="text-sm text-gray-600 dark:text-gray-400">
									{t('menuItems.spiceLevel')}
								</span>
							</div>
							<Badge
								variant={
									menuItem.spice_level === 'hot' || menuItem.spice_level === 'extra_hot'
										? 'error'
										: menuItem.spice_level === 'medium'
											? 'warning'
											: 'success'
								}
								size="sm"
							>
								{t(`menuItems.spice.${menuItem.spice_level}`)}
							</Badge>
						</div>
					)}
				</div>

				{/* Analytics Summary for Desktop */}
				{itemAnalytics.length > 0 && (
					<div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
						<div className="space-y-3">
							<div className="flex items-center justify-between">
								<div className="flex items-center gap-2">
									<FiMousePointer className="w-4 h-4 text-blue-500" />
									<span className="text-sm text-gray-600 dark:text-gray-400">
										{t('menuItems.avgClicks')}
									</span>
								</div>
								<span className="text-sm font-medium text-blue-600 dark:text-blue-400">
									{analyticsAverages.avgClicks}/mo
								</span>
							</div>
							<div className="flex items-center justify-between">
								<div className="flex items-center gap-2">
									<IoMdStopwatch className="w-4 h-4 text-green-500" />
									<span className="text-sm text-gray-600 dark:text-gray-400">
										{t('menuItems.avgTime')}
									</span>
								</div>
								<span className="text-sm font-medium text-green-600 dark:text-green-400">
									{analyticsAverages.avgTimeSpent}m
								</span>
							</div>
						</div>
					</div>
				)}
			</Card>
		</motion.div>
	)
}

export default ItemStatistics